import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Header, Input, Message, Pagination, Popup, Table } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { API, showError, showSuccess } from '../helpers';
import { ITEMS_PER_PAGE } from '../constants';

const ModelPricesTable = () => {
  const { t } = useTranslation();
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [total, setTotal] = useState(0);

  const loadPrices = async (startIdx) => {
    let url = `/api/model_price/?page=${startIdx}&page_size=${ITEMS_PER_PAGE}`;
    const res = await API.get(url);
    if (!res) {
      setLoading(false);
      return;
    }
    const { success, message, data, total } = res.data;
    if (success) {
      setPrices(data || []);
      setTotal(total || 0);
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const searchPrices = async () => {
    if (searchKeyword === '') {
      await loadPrices(0);
      setActivePage(1);
      return;
    }
    setSearching(true);
    const res = await API.get(`/api/model_price/search?keyword=${searchKeyword}&page=0&page_size=${ITEMS_PER_PAGE}`);
    if (!res) {
      setSearching(false);
      return;
    }
    const { success, message, data, total } = res.data;
    if (success) {
      setPrices(data || []);
      setTotal(total || 0);
    } else {
      showError(message);
    }
    setSearching(false);
  };

  const handleSearchChange = (e, { value }) => {
    setSearchKeyword(value);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      searchPrices();
    }
  };

  const onPaginationChange = (e, { activePage }) => {
    setActivePage(activePage);
    loadPrices(activePage - 1);
  };

  const deletePrice = async (id) => {
    const res = await API.delete(`/api/model_price/${id}`);
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('model_price.delete_success'));
      await loadPrices(activePage - 1);
    } else {
      showError(message);
    }
  };

  const refresh = async () => {
    setLoading(true);
    await loadPrices(activePage - 1);
  };

  useEffect(() => {
    loadPrices(0).then();
  }, []);

  return (
    <>
      <Form>
        <Form.Group>
          <Form.Input
            icon='search'
            placeholder={t('model_price.search_placeholder')}
            value={searchKeyword}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            action={{
              icon: 'search',
              onClick: searchPrices,
              loading: searching,
            }}
          />
          <Button color='green' as={Link} to='/model_price/add'>
            {t('model_price.add')}
          </Button>
          <Button color='blue' onClick={refresh} loading={loading}>
            {t('model_price.refresh')}
          </Button>
        </Form.Group>
      </Form>
      {prices.length === 0 && !loading ? (
        <Message info>{t('model_price.no_data')}</Message>
      ) : (
        <div className='table-scroll-wrap'>
        <Table basic='very' celled selectable compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell rowSpan='2'>{t('model_price.table.id')}</Table.HeaderCell>
              <Table.HeaderCell rowSpan='2'>{t('model_price.table.model_id')}</Table.HeaderCell>
              <Table.HeaderCell colSpan='3' textAlign='center' style={{ background: '#e8f4fd' }}>
                {t('model_price.section.sell')}
              </Table.HeaderCell>
              <Table.HeaderCell colSpan='3' textAlign='center' style={{ background: '#fff3e0' }}>
                {t('model_price.section.cost')}
              </Table.HeaderCell>
              <Table.HeaderCell rowSpan='2'>{t('model_price.table.remark')}</Table.HeaderCell>
              <Table.HeaderCell rowSpan='2'>{t('model_price.table.actions')}</Table.HeaderCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>{t('model_price.table.sell_prompt')}</Table.HeaderCell>
              <Table.HeaderCell>{t('model_price.table.sell_completion')}</Table.HeaderCell>
              <Table.HeaderCell>{t('model_price.table.sell_cache')}</Table.HeaderCell>
              <Table.HeaderCell>{t('model_price.table.cost_prompt')}</Table.HeaderCell>
              <Table.HeaderCell>{t('model_price.table.cost_completion')}</Table.HeaderCell>
              <Table.HeaderCell>{t('model_price.table.cost_cache')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {prices.map((price) => (
              <Table.Row key={price.id}>
                <Table.Cell>{price.id}</Table.Cell>
                <Table.Cell>
                  <strong>{price.model_id}</strong>
                </Table.Cell>
                <Table.Cell>{price.sell_prompt_ratio}</Table.Cell>
                <Table.Cell>{price.sell_completion_ratio}</Table.Cell>
                <Table.Cell>{price.sell_cache_ratio}</Table.Cell>
                <Table.Cell>{price.cost_prompt_ratio}</Table.Cell>
                <Table.Cell>{price.cost_completion_ratio}</Table.Cell>
                <Table.Cell>{price.cost_cache_ratio}</Table.Cell>
                <Table.Cell>{price.remark || '-'}</Table.Cell>
                <Table.Cell>
                  <Button
                    size='mini'
                    color='blue'
                    as={Link}
                    to={`/model_price/edit/${price.id}`}
                  >
                    {t('model_price.edit')}
                  </Button>
                  <Popup
                    trigger={
                      <Button size='mini' color='red'>
                        {t('model_price.delete')}
                      </Button>
                    }
                    on='click'
                    position='top right'
                  >
                    <Button
                      color='red'
                      content={t('model_price.confirm_delete')}
                      onClick={() => deletePrice(price.id)}
                    />
                  </Popup>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan='9'>
                <Pagination
                  floated='right'
                  activePage={activePage}
                  onPageChange={onPaginationChange}
                  totalPages={Math.ceil(total / ITEMS_PER_PAGE) || 1}
                />
                <span style={{ lineHeight: '36px' }}>
                  {t('model_price.total', { count: total })}
                </span>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
        </div>
      )}
    </>
  );
};

export default ModelPricesTable;
